import React, { useEffect, useState, useRef } from "react";
import { Box, Container, Grid, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableRow, Button, Modal, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useParams } from "react-router-dom";
import { Pay } from "../../../../models/pay";
import { Lend } from "../../../../models/lend";
import { Console } from "console";

interface InvoiceProps {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  showAlert: (message: string, severity?: "info" | "error" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
  payId: number; //
}

const Invoice: React.FC<InvoiceProps> = ({ isOpen, onClose, clientName, showAlert, showLoading, hideLoading, payId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { id } = useParams();
  const [pays, setPays] = useState<Pay | null>(null);
  const [lend, setLend] = useState<Lend | null>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: paysData } = await axios.get(`pays/${payId}`);
        setPays(paysData);
        if (paysData.lend.id) {
          const { data: lendData } = await axios.get(`lends/${paysData.lend.id}`);
          setLend(lendData);
        }
      } catch (error) {
        showAlert("Error fetching data:", "error");
      } finally {
        hideLoading();
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [id, isOpen, showAlert, showLoading, hideLoading]);

  const formattedDate = pays?.date ? pays.date.slice(0, 10).replaceAll("-", "") : 'Fecha no disponible';
  const formattedTime = pays?.date ? pays.date.slice(11, 19).replaceAll(":", "").replace(".", "") : 'Hora no disponible';

  const tcktNmbr = `${pays?.id}`;

  const hidePdfButtons = () => {
    document.querySelectorAll(".pdf-hide").forEach((element) => {
      (element as HTMLElement).style.display = "none";
    });
  };

  const showPdfButtons = () => {
    document.querySelectorAll(".pdf-hide").forEach((element) => {
      (element as HTMLElement).style.display = "inline-block";
    });
  };

  async function handlePrint() {
    if (invoiceRef.current && lend?.client?.fullname && pays) {
      const clientName = lend.client.fullname.replace(/\s+/g, "_");
      const fileName = `${clientName}-Recibo-${tcktNmbr}.pdf`;

      hidePdfButtons();
      await new Promise((resolve) => setTimeout(resolve, 300));

      const pdfWidth = 75; // Ancho en mm (7.5 cm)
      const canvas = await html2canvas(invoiceRef.current!, {
        scale: 4,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: invoiceRef.current!.scrollWidth,
        height: invoiceRef.current!.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidth, imgHeight],
      });

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight, undefined, "FAST");
      pdf.save(fileName);

      showPdfButtons();
    }
  }

  async function handleSharePDF() {
    if (invoiceRef.current && clientName) {
      const formattedClientName = clientName.replace(/\s+/g, "_");
      const fileName = `${formattedClientName}-Recibo-${tcktNmbr}.pdf`;

      hidePdfButtons();
      await new Promise((resolve) => setTimeout(resolve, 300));

      const pdfWidth = 75;
      const canvas = await html2canvas(invoiceRef.current!, {
        scale: 4,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width: invoiceRef.current!.scrollWidth,
        height: invoiceRef.current!.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [pdfWidth, imgHeight],
      });

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeight, undefined, "FAST");
      const pdfBlob = pdf.output("blob");

      try {
        if (navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], fileName, { type: "application/pdf" })] })) {
          const file = new File([pdfBlob], fileName, { type: "application/pdf" });
          await navigator.share({
            title: "Compartir PDF",
            text: "Aquí tienes el PDF generado.",
            files: [file],
          });
        } else {
          const pdfUrl = URL.createObjectURL(pdfBlob);
          window.open(pdfUrl, "_blank");
          showAlert("El navegador no soporta la funcionalidad de compartir archivos.", "info");
        }
      } catch (error) {
        showAlert("Error al generar o compartir el PDF.", "error");
      }

      showPdfButtons();
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={{ width: "375px", margin: "0 auto", height: "100%", maxHeight: "90vh", overflowY: "auto" }} ref={invoiceRef}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom textAlign="center">
              Comprobante de Pago
            </Typography>
            <Typography variant="body2" textAlign="right">
              Recibo #{tcktNmbr}
            </Typography>
            <Grid container spacing={2}>
              <Grid item sm={6}>
                {lend?.client && (
                  <dl>
                    <dt><strong>Nombre:</strong> {lend.client.fullname}</dt>
                    <dt><strong>Identificación:</strong> {lend.client.idnumber}</dt>
                    <dt><strong>Dirección:</strong> {lend.client.mainaddress}</dt>
                  </dl>
                )}
              </Grid>
              <Grid item sm={6} textAlign="right">
                <strong>Fecha de Pago:</strong>
                <br />
                {pays?.date} {pays?.date.slice(11, 16)}
              </Grid>
            </Grid>
            <Typography variant="h6" gutterBottom>Resumen de Pago</Typography>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell align="left">Valor Total: {lend?.total_value}</TableCell>
                    <TableCell align="right">Tipo de Pago: Efectivo</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Cuota #{pays?.id}</TableCell>
                    <TableCell align="right">Valor cuota: ${pays?.value}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent={isMobile ? "center" : "flex-end"} flexDirection={isMobile ? "column" : "row"} mt={2}>
          <Button variant="contained" className="pdf-hide" sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0 }} onClick={handlePrint}>
            Descargar
          </Button>
          <Button variant="contained" className="pdf-hide" onClick={handleSharePDF}>
            Compartir
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default Invoice;
